import { useQuery } from '@tanstack/react-query';
import { createServerFn } from '@tanstack/react-start';
import z from 'zod';

import { ELEVATED_ROLES } from '@/lib/constants/auth.constants';
import { handleServerFnError } from '@/lib/sentry-helpers';
import { standardMiddlewares } from '@/middlewares/standardMiddlewares';
import { assertSelfOrRole } from '@/middlewares/userAuthMiddleware';
import { OneRosterUser } from '@/types/user.oneroster';

export const parentStudentsQueryKey = (userId?: string | null) => [
  'parentStudents',
  userId,
];

export function useParentStudents(userId?: string | null) {
  const {
    data: students,
    isLoading,
    error,
  } = useQuery({
    queryKey: parentStudentsQueryKey(userId),
    queryFn: async () => {
      if (!userId) {
        return [];
      }
      const response = await getParentStudents({
        data: { userId },
      });

      return response;
    },
  });

  return {
    students,
    isLoading,
    error,
  };
}

const getParentStudents = createServerFn()
  .middleware([standardMiddlewares])
  .validator(z.object({ userId: z.string() }))
  .handler(async ({ context, data }) => {
    const { cognitoUser, oneRosterApiTyped } = context;
    await assertSelfOrRole(cognitoUser, oneRosterApiTyped, data.userId, ELEVATED_ROLES);

    try {
      const response = await oneRosterApiTyped.GET(
        `/ims/oneroster/rostering/v1p2/users/{userId}/agentFor`,
        { params: { path: { userId: data.userId } } }
      );

      return response.data?.users as unknown as OneRosterUser[];
    } catch (error) {
      return handleServerFnError(error, {
        service: 'oneroster-api',
        function: 'getParentStudents',
        extra: {
          userId: data.userId,
        },
      });
    }
  });
